import { Section, Eyebrow } from "./Layout";
import type { ReactNode } from "react";

export type FAQItem = { q: string; a: string };

export function FAQSection({
  items,
  title = "Frequently asked questions.",
  subtitle,
  eyebrow = "FAQ",
  addSchema = false,
  tone = "light",
  renderAnswer,
  semanticQuestionHeadings = false,
}: {
  items: FAQItem[];
  title?: string;
  subtitle?: string;
  eyebrow?: string;
  addSchema?: boolean;
  tone?: "light" | "muted";
  renderAnswer?: (answer: string) => ReactNode;
  semanticQuestionHeadings?: boolean;
}) {
  if (!items || items.length === 0) return null;
  const wrapper =
    tone === "muted"
      ? "bg-secondary/40 border-y border-border"
      : "bg-background";
  return (
    <div className={wrapper}>
      <Section>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h2 className="mt-4 text-4xl font-semibold text-foreground max-w-3xl">{title}</h2>
        {subtitle && <p className="mt-4 text-lg text-muted-foreground max-w-3xl">{subtitle}</p>}
        <div className="mt-10 space-y-4 max-w-3xl">
          {items.map((f) => (
            <details key={f.q} className="group rounded-xl border border-border bg-background p-6">
              <summary className="cursor-pointer font-semibold text-lg text-foreground flex justify-between items-center gap-6">
                {semanticQuestionHeadings ? <h3 className="font-inherit text-inherit">{f.q}</h3> : f.q}
                <span className="text-[var(--gold-deep)] group-open:rotate-45 transition text-2xl leading-none">+</span>
              </summary>
              <p className="mt-4 text-muted-foreground leading-relaxed">{renderAnswer ? renderAnswer(f.a) : f.a}</p>
            </details>
          ))}
        </div>
        {addSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: items.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              }),
            }}
          />
        )}
      </Section>
    </div>
  );
}